import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, ShieldCheck, CheckCircle2, KeyRound, Loader2 } from 'lucide-react';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { PasswordInput } from '../components/brand/PasswordInput';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error('Password must be at least 8 characters.');
    if (password !== confirm) return toast.error('Passwords do not match.');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success('Password reset. You can now sign in.');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cn-bg flex flex-col" data-testid="reset-password-page">
      <header className="px-6 md:px-10 py-5 flex items-center justify-between border-b border-cn-border">
        <Link to="/"><CaraLoomLogo /></Link>
        <Link to="/auth" className="inline-flex items-center gap-1.5 text-sm text-cn-text-muted hover:text-cn-primary" data-testid="reset-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="w-12 h-12 rounded-2xl bg-cn-soft flex items-center justify-center mb-5">
            <KeyRound className="w-6 h-6 text-cn-primary" />
          </div>

          {!token ? (
            <div className="cn-card p-7 rounded-3xl" data-testid="reset-no-token">
              <h1 className="font-display text-2xl font-bold text-cn-text mb-2">Reset link missing</h1>
              <p className="text-cn-text-muted mb-5">This page needs a valid reset link. Request a new one from the sign-in screen.</p>
              <Link to="/auth" className="cn-btn cn-btn-primary">Go to sign in <ArrowRight className="w-4 h-4" /></Link>
            </div>
          ) : done ? (
            <div className="cn-card p-8 rounded-3xl text-center" data-testid="reset-success">
              <div className="w-14 h-14 rounded-full bg-cn-soft flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-cn-primary" />
              </div>
              <h1 className="font-display text-2xl font-bold text-cn-text mb-2">Password reset</h1>
              <p className="text-cn-text-muted mb-6">Your password has been updated. Sign in with your new password.</p>
              <button onClick={() => navigate('/auth')} className="cn-btn cn-btn-primary w-full h-12" data-testid="reset-go-signin">
                Sign in <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <div className="cn-eyebrow mb-2">Choose a new password</div>
              <h1 className="font-display text-3xl font-bold leading-tight text-cn-text">Reset your password</h1>
              <p className="mt-3 text-cn-text-muted">Pick a strong password you don't use elsewhere. Use the eye to check it.</p>

              <form onSubmit={submit} className="mt-7 space-y-4" data-testid="reset-form">
                <div>
                  <Label htmlFor="new-password" className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">New password</Label>
                  <PasswordInput
                    id="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    testid="reset-new-password"
                    autoFocus
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password" className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Confirm password</Label>
                  <PasswordInput
                    id="confirm-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    testid="reset-confirm-password"
                  />
                  {confirm && confirm !== password && (
                    <p className="text-xs text-cn-error mt-1.5" data-testid="reset-mismatch">Passwords don't match yet.</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || password.length < 8 || password !== confirm}
                  className="cn-btn cn-btn-primary w-full h-12 text-base disabled:opacity-60"
                  data-testid="reset-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Reset password <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-xs text-cn-text-muted text-center flex items-center justify-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-cn-primary" /> Reset links are single-use and expire in 1 hour.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
